import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import reduce from 'lodash/reduce';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import escape from 'lodash/escape';
import identity from 'lodash/identity';
import last from 'lodash/last';
import get from 'lodash/get';
import {Map, List} from 'immutable';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {transformers} from '@packages/systems/dynamo/utils/Transformers';
import type {
  CollectionsType,
  ItemsType,
} from '@packages/systems/core/records/CollectionStateType';
import {
  type Token,
  getWfTokenPattern,
  getCatchAllTokenPattern,
  getExternalTokenPattern,
  parseTokenJson,
  extractToken,
  stripLegacyShorthandSuffix,
} from '@packages/systems/core/utils/EmbedUtils/shared';
import {type EmbedTokenOption} from '@packages/systems/core/utils/EmbedUtils/types';

type TransformFieldValueOptions = {
  dangerouslyUnescaped?: boolean;
  timezone?: string;
  // importing CurrencySettings type introduces a circular dependency
  currencySettings?: any;
};

type ReplaceTokensOptions = TransformFieldValueOptions & {
  options: EmbedTokenOption[];
  cleanSlug?: (arg1: string, arg2?: number | null | undefined) => string;
};

type ReplaceTokensWithExpansionOptions = ReplaceTokensOptions & {
  collections: CollectionsType;
  items: ItemsType;
};

type ReplaceTokenFunction = (arg1: Token) => string;

/**
 * Replaces tokens in text with field values from a POJO item.
 *
 * Tokens to be replaced can be one of the following:
 * - {{wf {"path":"name"} }} tokens are replaced with field values or an empty string if value does not exist
 * - {{ name }} tokens are replaced with field values (requires includeShorthands and options) åor an empty string if value does not exist
 * - {\{ custom }} tokens are unescaped to {{ custom }} for allowing users to input custom stuff without us replacing it
 * @param  {String}   replaceable           The string with tokens
 * @param  {Object}   item                  POJO item whose fields values are used in token replacement
 * @param  {List}     options               List of available tokens that are needed when replacing legacy tokens
 * @param  {Boolean}  dangerouslyUnescaped  Use with caution. Boolean value defining if token values should be rendered without escaping.
 * @param  {Function} cleanSlug             Optional function that can be used for mapping token paths to item slugs. Handy e.g. with GraphQL item slugs.
 * @param  {String}   timezone              Site locality timezone
 * @param  {Object}   currencySettings      Site's currency settings to use for price rendering
 * @return {String}                         Text with tokens replaced with field values
 */
export function replaceTokens(
  replaceable: string,
  item:
    | Readonly<{
        [key: string]: any;
      }>
    | null
    | undefined,
  {
    // @ts-expect-error - TS2322 - Type 'void' is not assignable to type 'EmbedTokenOption[]'.
    options = throwIfMissing('options'),
    dangerouslyUnescaped = false,
    cleanSlug = identity,
    timezone,
    currencySettings,
  }: ReplaceTokensOptions
) {
  return _replaceTokens(replaceable, replaceToken, options);

  function replaceToken(token: Token) {
    if (!token || !item) {
      return '';
    }

    const {path = ''} = token;
    const fieldPath = path.split(/\.|:/);
    const fieldValue = get(item, fieldPath.map(cleanSlug));
    return transformFieldValue(fieldValue, token, {
      dangerouslyUnescaped,
      timezone,
      currencySettings,
    });
  }
}

/**
 * Replaces tokens in text with field values from an Immutable item.
 *
 * Expands ItemRef fields and Option fields of the provided item if necessary.
 *
 * Tokens to be replaced can be one of the following.
 * - {{wf {"path":"name"} }} tokens are replaced with field values or an empty string if value does not exist
 * - {{ name }} tokens are replaced with field values (requires includeShorthands and options) åor an empty string if value does not exist
 * - {\{ custom }} tokens are unescaped to {{ custom }} for allowing users to input custom stuff without us replacing it
 * @param  {String}          replaceable           The string with tokens
 * @param  {ItemRecordType}  item                  Immutable item whose fields are used in token replacement
 * @param  {Map}             collections           Flux collectionStore's collections
 * @param  {Map}             items                 Flux collectionStore's items
 * @param  {List}            options               List of available tokens that are needed when replacing legacy tokens
 * @param  {Boolean}         dangerouslyUnescaped  Use with caution. Boolean value defining if token values should be rendered without escaping.
 * @param  {Function}        cleanSlug             Optional function that can be used for mapping token paths to item slugs. Handy e.g. with GraphQL item slugs.
 * @param  {String}          timezone              Site locality timezone
 * @param  {Object}          currencySettings      Site's currency settings to use for price rendering
 * @return {String}                                Text with tokens replaced with field values
 */
export function replaceTokensWithExpansion(
  replaceable: string,
  item: ItemRecordType | null | undefined,
  {
    // @ts-expect-error - TS2322 - Type 'void' is not assignable to type 'EmbedTokenOption[]'.
    options = throwIfMissing('options'),
    // @ts-expect-error - TS2322 - Type 'void' is not assignable to type 'CollectionsType'.
    collections = throwIfMissing('collections'),
    // @ts-expect-error - TS2322 - Type 'void' is not assignable to type 'ItemsType'.
    items = throwIfMissing('items'),
    dangerouslyUnescaped = false,
    cleanSlug = identity,
    timezone,
    currencySettings,
  }: ReplaceTokensWithExpansionOptions
) {
  return _replaceTokens(replaceable, replaceToken, options);

  function replaceToken(token: Token) {
    if (!token || !item) {
      return '';
    }

    const {path = ''} = token;
    const pathParts = path.split(':').map((part) => part.split('.'));

    let _item = item;
    let _path = pathParts[0];

    // expand item reference fields
    const isItemRef = pathParts.length === 2;
    if (isItemRef) {
      // @ts-expect-error - TS2769 - No overload matches this call.
      const refItemId = item.getIn(pathParts[0]);
      if (!refItemId) {
        // Context doesn't have an item ref value set.
        return '';
      }
      // refItemId can be expanded or an id.
      const refItem = isObject(refItemId) ? refItemId : items.get(refItemId);

      // @ts-expect-error TS2740: Type '{}' is missing the following properties from type 'Map<string, any>': set, delete, remove, clear, and 91 more.
      _item = refItem;
      _path = pathParts[1];
    }

    if (!_item) {
      return '';
    }

    // @ts-expect-error - TS18048 - '_path' is possibly 'undefined'.
    const fieldValue = _item.getIn(_path.map(cleanSlug));
    let _fieldValue = fieldValue;

    // expand option fields
    if (token.type === 'Option' && isString(fieldValue)) {
      const collection = collections.get(_item.get('_cid'));
      const fields = collection && collection.get('fields');
      const optionField =
        fields && fields.find((field) => field.get('slug') === last(_path));
      const fieldOptions =
        optionField && optionField.getIn(['validations', 'options']);
      const option =
        fieldOptions &&
        // @ts-expect-error - TS2339 - Property 'find' does not exist on type '{}'. | TS7006 - Parameter 'opt' implicitly has an 'any' type.
        fieldOptions.find((opt) => opt.get('id') === fieldValue);
      _fieldValue = option;
    }

    return transformFieldValue(_fieldValue, token, {
      dangerouslyUnescaped,
      timezone,
      currencySettings,
    });
  }
}

function _replaceTokens(
  replaceable: string,
  replaceTokenFn: ReplaceTokenFunction,
  tokenOptions: EmbedTokenOption[]
) {
  return replaceable
    .replace(getWfTokenPattern(), doReplacements)
    .replace(getCatchAllTokenPattern(), doShorthandReplacements)
    .replace(getExternalTokenPattern(), '{{$1}}');

  function doReplacements(match: string) {
    const token = parseTokenJson(match);

    if (!token) {
      return '';
    }

    const {path: tokenPath, type: tokenType} = token;

    const isValidToken = tokenOptions.some((tokenOption) => {
      const parsedOption = JSON.parse(tokenOption.value);
      return parsedOption.path === tokenPath && parsedOption.type === tokenType;
    });

    return isValidToken ? replaceTokenFn(token) : '';
  }

  function doShorthandReplacements(match: string, withoutBraces: any) {
    const shorthandOption = tokenOptions.find(
      (option) =>
        JSON.parse(option.value).path ===
        stripLegacyShorthandSuffix(withoutBraces)
    );
    const shorthand = shorthandOption
      ? createTokenFromString(shorthandOption.value)
      : match;
    // @ts-expect-error - TS2345 - Argument of type 'Token | null' is not assignable to parameter of type 'Token'.
    return replaceTokenFn(parseTokenJson(shorthand));
  }
}

export function removeAllTokens(replaceable: any) {
  return replaceable
    .replace(getCatchAllTokenPattern(), '')
    .replace(getExternalTokenPattern(), '{{$1}}');
}

export function removeWfTokens(replaceable: any) {
  return replaceable.replace(getWfTokenPattern(), '');
}

export function replaceExternalTokens(replaceable: any) {
  return replaceable.replace(getExternalTokenPattern(), '{{$1}}');
}

/**
 * Applies token transformers (e.g. date format) one by one to a given value.
 * @param  {*}        value    Value that transformers are applied to
 * @param  {Object}   token    Token {path, transformers, type}
 * @param  {timezone} string   Moment-compatible timezone string
 * @return {*}        transformed value
 */
export function applyTransformers(
  value: any,
  token: Token | Record<any, any>,
  timezone: string = 'UTC',
  currencySettings?: any
) {
  let transformedValue = getRealValue(value, token.type || '');
  if (Array.isArray(token.transformers)) {
    token.transformers.forEach((transformer) => {
      if (transformer.name !== 'date') {
        // We don't have embed transformers other than 'date'. However,
        // the transformers are read from a user-defined embed token string
        // so we need to filter out any other types of transformers.
        return;
      }
      const format = transformer.arguments && transformer.arguments[0];
      transformedValue = format
        ? transformers(
            transformedValue,
            {type: 'date', params: [format]},
            {timezone, collectionSlugMap: emptyObject, currencySettings}
          )
        : transformedValue;
    });
  }

  if (token.type === 'CommercePrice') {
    transformedValue = transformers(
      transformedValue,
      {type: 'price', params: emptyArray},
      {collectionSlugMap: emptyObject, currencySettings}
    );
  }

  return transformedValue == null ? '' : transformedValue;
}

/**
 * Checks if token string is a valid token that matches with given token option
 * @param  {String}  tokenString         A token string, e.g. 'wf {&quot;path&quot;:&quot;name&quot;\}'
 * @param  {Map}  option              A token option {label, value, type}
 * @param  {Boolean} includeShorthands   A flag that tells that token string is in legacy format, e.g. {{ name }}
 * @return {Boolean} Boolean value telling if token string matches given option
 */
export function tokenMatchesOption(
  tokenString: string,
  option: EmbedTokenOption
) {
  const isShortHand = !tokenString.match(getWfTokenPattern());
  if (
    isShortHand &&
    extractToken(tokenString, {shortHand: true}) ===
      JSON.parse(option.value).path
  ) {
    return true;
  }
  const matchJsonString = JSON.stringify(parseTokenJson(tokenString));
  return matchJsonString === option.value;
}

/**
 * Returns an array of token path arrays that are extracted from tokens that match
 * the token pattern.
 *
 * NOTE:
 * This function does not validate whether the path matches any collection field.
 * Top-level GraphQL validates the token paths against GraphQL schema and does not
 * know about collection fields.
 *
 * @param {String} text
 * @returns {Array<Array<String>>} paths
 */
export function getTokenPathsMatchingPathPattern(
  text: string
): Array<Array<string>> {
  return reduce<string, string[][]>(
    text.match(getCatchAllTokenPattern()) || [],
    (paths, match) => {
      const tokenPath = getTokenPath(match);

      if (tokenPath && isTokenPathWithValidPattern(tokenPath)) {
        paths.push(tokenPath.split(':'));
      }

      return paths;
    },
    []
  );
}

const getTokenPath = (tokenString: string): string | null => {
  if (getCatchAllTokenPattern().test(tokenString) === false) {
    // Not a token so we can return early
    return null;
  }
  const isShortHand = !getWfTokenPattern().test(tokenString);

  if (isShortHand) {
    // Token is a legacy shorthand token such as {{name}} or {{author:name}}
    return extractToken(tokenString, {shortHand: true});
  } else {
    // Token is a wf token such as
    // {{wf {&quot;path&quot;:&quot;name&quot;,&quot;type&quot;:&quot;PlainText&quot;\} }} or
    // {{wf {&quot;path&quot;:&quot;author:name&quot;,&quot;type&quot;:&quot;PlainText&quot;\} }}
    const token = parseTokenJson(tokenString);
    return token && token.path ? token.path : null;
  }
};

const isTokenPathWithValidPattern = (tokenPath: string): boolean => {
  return /[^a-zA-Z0-9-:]/.test(tokenPath) === false;
};

export function getTokenUsages(
  text: any,
  tokenOptions: EmbedTokenOption[]
): Map<string, number> {
  return reduce(
    text.match(getCatchAllTokenPattern()) || [],
    (usages, match) => {
      const matchingOption = tokenOptions.find((option) =>
        tokenMatchesOption(match, option)
      );
      if (!matchingOption) {
        return usages;
      }
      const token = parseTokenJson(createTokenFromString(matchingOption.value));
      return token
        ? usages.update(token.path, 0, (count) => count + 1)
        : usages;
    },
    Map()
  );
}

export function isItemRefTokenPath(tokenPath: any) {
  return /:/.test(tokenPath);
}

/**
 * Checks if embed content has tokens. Looks for all three kinds ({{wf {"path":"name"} }}, {{ name }} and {\{ custom }}) of tokens.
 * @param  {String}  value   Embed content
 * @return {Boolean} Boolean value telling if token has tokens
 */
export function embedHasTokens(value: any) {
  const hasWebflowTokens = Boolean(value.match(getCatchAllTokenPattern()));
  const hasExternalTokens = Boolean(value.match(getExternalTokenPattern()));
  return hasWebflowTokens || hasExternalTokens;
}

export function escapeTokenString(string: any) {
  // @ts-expect-error - TS7006 - Parameter 'match' implicitly has an 'any' type. | TS7006 - Parameter 'withoutBraces' implicitly has an 'any' type.
  return string.replace(getWfTokenPattern(), (match, withoutBraces) =>
    createTokenFromString(escape(withoutBraces.replace(/}/g, '\\}')))
  );
}

export function createTokenFromString(string: any) {
  // Need to make sure that we don't end up with '{{wf  }}', which would match a custom field with slug 'wf'.
  return '{{wf ' + (string == null ? 'undefined' : string) + ' }}';
}

function transformFieldValue(
  fieldValue: any,
  token: Token,
  {dangerouslyUnescaped, timezone, currencySettings}: TransformFieldValueOptions
): any {
  const _escape = dangerouslyUnescaped ? identity : escape;
  if (token.type === 'Bool') {
    // TODO can be removed once backend casts unset boolean fields to false as default
    return isString(fieldValue) ? fieldValue === 'true' : Boolean(fieldValue);
  }
  return fieldValue == null
    ? ''
    : _escape(applyTransformers(fieldValue, token, timezone, currencySettings));
}

function getRealValue(value: any, type: string) {
  if (type === 'ImageRef' || type === 'FileRef') {
    return getImmOrPojo(value, 'url');
  } else if (type === 'Option') {
    return getImmOrPojo(value, 'name');
  } else if (type === 'HighlightedText') {
    return List.isList(value) || Array.isArray(value)
      ? value.map((part) => getImmOrPojo(part, 'value')).join('')
      : '';
  } else {
    return value;
  }
}

function throwIfMissing(name: string) {
  throw new Error('Missing parameter' + (name ? ' ' + name : ''));
}

const getImmOrPojo = (obj: any, key: string) =>
  typeof obj.get === 'function' ? obj.get(key) : obj[key];
